import { FC, MouseEventHandler, useMemo } from 'react';
import { css } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { PersonsV3 } from '@frontend/api-person';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions } from '@frontend/contact-actions';
import { useGlobalSearch } from '@frontend/global-search';
import { Icon } from '@frontend/icons';
// TODO: need to convert the @frontend/inbox to a widget library type or move out this utility
import { getPersonV3SecondaryInfoString } from '@frontend/inbox';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { useAppScopeStore } from '@frontend/scope';
import { convertStringToMatchedWords, getPhoneMatches, TextWithMatches } from '@frontend/search-history';
import { useContactPanelShallowStore } from '@frontend/shared';
import type { CommonHTMLLIAttributes } from '@frontend/types';
import { ResponsivePetChips, useGetActivePetsQuery } from '@frontend/vet-components';
import { theme } from '@frontend/theme';
import { NakedButton, Text, styles } from '@frontend/design-system';

type ContactSearchResultProps = {
  person: Person;
  searchValue: string;
  onSelect: MouseEventHandler<HTMLButtonElement>;
  isGlobalSearch?: boolean;
  trackingId?: string;
};

export const ContactSearchResult: FC<CommonHTMLLIAttributes & ContactSearchResultProps> = ({
  person,
  searchValue,
  isGlobalSearch = false,
  onSelect,
  trackingId,
  ...rest
}) => {
  const personName = getFullName(person);
  const nameParts = convertStringToMatchedWords(personName, [searchValue.trim()]);
  const { setPersonId } = useContactPanelShallowStore('setPersonId');
  const phoneNumbers = getPhoneMatches(searchValue, person.contactInfo);
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const isVet = lastUsedVertical === 'VET';
  const { close } = useGlobalSearch(['close']);
  const { selectedLocationIds, getScopeName } = useAppScopeStore();
  const { getLocationsByDatasource } = DataSourcesHooks.useDatasourceToLocations(selectedLocationIds, getScopeName);
  const locations = getLocationsByDatasource(person.sourceId || '', undefined);
  const locationId = locations?.[0]?.locationId;
  const selectedLocationId = selectedLocationIds[0];

  const callContext = useMemo(() => PersonsV3.PersonHelpers.convertPersonV3ToPerson(person), [person]);

  const messageContext = useMemo(() => {
    return {
      personId: person.personId,
      threadGroupId: locationId ?? selectedLocationId,
      groupIds: locationId ? [locationId] : [selectedLocationId],
    };
  }, [locationId, person.personId, selectedLocationId]);

  return (
    <li css={isGlobalSearch && { minWidth: 290, flex: 1 }} {...rest}>
      <NakedButton
        trackingId={trackingId}
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          borderRadius: theme.borderRadius.small,
          padding: theme.spacing(2),
          border: `1px solid ${theme.colors.neutral10}`,
          width: '100%',
          height: '100%',
          alignItems: 'center',
          transition: 'background-color 250ms ease-in-out',
          ':hover': {
            backgroundColor: theme.colors.neutral5,
          },
        }}
        onClick={(e) => {
          setPersonId(person.personId, true, locations?.[0]?.locationId ?? selectedLocationIds[0]);
          onSelect(e);
        }}
      >
        <Photos.ContactProfilePhoto
          locationId={locations?.[0]?.locationId ?? selectedLocationIds[0] ?? ''}
          personId={person.personId}
          firstName={person.firstName}
          lastName={person.lastName}
        />
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            overflow: 'hidden',
            marginLeft: theme.spacing(1),
          }}
        >
          <TextWithMatches text={nameParts} />
          {!!phoneNumbers.length && (
            <div
              css={css`
                margin-bottom: ${theme.spacing(0.25)};
              `}
            >
              {phoneNumbers.map((phone) => (
                <Text
                  key={phone.phoneNumber}
                  size='small'
                  weight='bold'
                  textAlign='left'
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: ${theme.spacing(0.25)};
                  `}
                >
                  <Icon name={phone.iconName} size={16} />
                  <span>{phone.phoneNumber}</span>
                </Text>
              ))}
            </div>
          )}
          {isVet ? (
            <VetContactSearchResultContent
              locationId={locations?.[0]?.locationId ?? selectedLocationIds[0] ?? ''}
              person={person}
            />
          ) : (
            <ContactSearchResultContent person={person} />
          )}
        </div>
        <ContactSearchResultActions>
          <Actions.Message
            label={'Message'}
            context={messageContext}
            onClick={close}
            trackingIdSuffix='contact-panel'
          />
          <Actions.Call
            label={'Call'}
            onClick={close}
            context={{ person: callContext }}
            trackingIdSuffix='profile-contact-actions'
          />
        </ContactSearchResultActions>
      </NakedButton>
    </li>
  );
};

const ContactSearchResultActions: FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AdaptoActions
      css={css`
        margin-left: auto;
      `}
      size='small'
      iconColor='default'
      variant='icon-buttons'
    >
      {children}
    </AdaptoActions>
  );
};

const ContactSearchResultContent = ({ person }: { person: Person }) => (
  <Text
    color='light'
    size='small'
    textAlign='left'
    css={[
      css`
        max-width: 100%;
      `,
      styles.truncate,
    ]}
  >
    {getPersonV3SecondaryInfoString(person)}
  </Text>
);

const VetContactSearchResultContent = ({ person, locationId }: { person: Person; locationId: string }) => {
  const petQuery = useGetActivePetsQuery({ locationId, personId: person.personId || '' });
  return <ResponsivePetChips pets={petQuery.data || []} maxPets={1} />;
};
